import type {
  Asset,
  AssetStatus,
  AssetType,
  ServiceType,
} from "../../types";
import { formatDate } from "../../utils/assetDates";
import styles from "./ClientAssetModule.module.css";

type Props = {
  type: AssetType;
  assets: Asset[];
  onView: (asset: Asset) => void;
  onEdit: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
};

function statusClass(status: AssetStatus) {
  return {
    Operational: styles.operational,
    "Due Soon": styles.dueSoon,
    "Under Service": styles.underService,
    Expired: styles.expired,
  }[status];
}

function ServiceDate({ asset, type }: { asset: Asset; type: ServiceType }) {
  const schedule = asset.services[type];
  return (
    <span className={styles.serviceDate}>
      <strong>
        {schedule?.nextServiceLabel || formatDate(schedule?.nextService)}
      </strong>
      <small>
        Last: {schedule?.lastServiceLabel || formatDate(schedule?.lastService)}
      </small>
    </span>
  );
}

export default function AssetTable({
  type,
  assets,
  onView,
  onEdit,
  onDelete,
}: Props) {
  const tableClass = `${styles.tableWrapper} ${
    assets.length > 10 ? styles.scrollable : ""
  }`;

  return (
    <div className={tableClass}>
      <table className={type === "Life Boat" ? styles.boatTable : undefined}>
        <thead>
          {type === "Life Raft" ? (
            <tr>
              <th>Asset</th>
              <th>Platform</th>
              <th>Pax</th>
              <th>Brand / Serial</th>
              <th>Current location</th>
              <th>Year</th>
              <th>Life raft service</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          ) : (
            <tr>
              <th>Asset</th>
              <th>Location</th>
              <th>IMO number</th>
              <th>Model / Units</th>
              <th>Annual inspection</th>
              <th>5 yearly</th>
              <th>Refurbishment</th>
              <th>Hooks / Wire / Davit / Winch</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          )}
        </thead>
        <tbody>
          {assets.map((asset) =>
            type === "Life Raft" ? (
              <tr key={asset.id}>
                <td>
                  <strong>{asset.assetId}</strong>
                  <span>{asset.name}</span>
                </td>
                <td>{asset.vessel || "—"}</td>
                <td>{asset.capacity ? `${asset.capacity} pax` : "—"}</td>
                <td>
                  <strong>{asset.manufacturer || "—"}</strong>
                  <span>{asset.serial || "No serial"}</span>
                </td>
                <td>{asset.location || "—"}</td>
                <td>{asset.yearOfManufacture || "—"}</td>
                <td>
                  <ServiceDate asset={asset} type="life_raft_service" />
                </td>
                <td>
                  <span className={`${styles.status} ${statusClass(asset.status)}`}>
                    {asset.status}
                  </span>
                </td>
                <td>
                  <RowActions
                    asset={asset}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </td>
              </tr>
            ) : (
              <tr key={asset.id}>
                <td>
                  <strong>{asset.assetId}</strong>
                  <span>{asset.name}</span>
                </td>
                <td>{asset.vessel || "—"}</td>
                <td>{asset.imoNumber || "—"}</td>
                <td>
                  <strong>{asset.model || asset.manufacturer || "—"}</strong>
                  <span>{asset.unitCount} unit(s)</span>
                </td>
                <td>
                  <ServiceDate asset={asset} type="annual_inspection" />
                </td>
                <td>
                  <ServiceDate asset={asset} type="five_yearly" />
                </td>
                <td>
                  <ServiceDate asset={asset} type="refurbishment" />
                </td>
                <td>
                  <ServiceDate
                    asset={asset}
                    type="hooks_wire_davit_winch"
                  />
                </td>
                <td>
                  <span className={`${styles.status} ${statusClass(asset.status)}`}>
                    {asset.status}
                  </span>
                </td>
                <td>
                  <RowActions
                    asset={asset}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

function RowActions({
  asset,
  onView,
  onEdit,
  onDelete,
}: {
  asset: Asset;
  onView: (asset: Asset) => void;
  onEdit: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
}) {
  return (
    <span className={styles.actions}>
      <button type="button" onClick={() => onView(asset)}>
        View
      </button>
      <button type="button" onClick={() => onEdit(asset)}>
        Edit
      </button>
      <button
        type="button"
        className={styles.deleteButton}
        onClick={() => onDelete(asset)}
      >
        Delete
      </button>
    </span>
  );
}
